/**
 * Review Feedback System Usage:
 *
 * To trigger feedback from other components:
 * 1. Import useAppState: import useAppState from "@/contexts/state";
 * 2. Get the methods: const { setReviewFeedback, setShowReviewFeedbackDialog } = useAppState();
 * 3. Set the feedback data: setReviewFeedback({ ai_tool_used: 'shorten', selected_text: 'some text', feedback_source: 'thumbs_down' });
 * 4. Open the dialog: setShowReviewFeedbackDialog(true);
 *
 * The dialog will automatically reset the feedback data when closed.
 */

import { Button, Modal, ModalBody, ModalContent, ModalFooter, ModalHeader, Textarea } from "@heroui/react"
import { useEffect, useState } from "react";
import useAppState from "@/contexts/state";
import { AuthState } from "@/contexts/slices/auth";
import { useParams } from "react-router-dom";
import toast from "react-hot-toast";
import { Button as ShadCnButton } from "@/components/ui/button";
import { Icon } from "@iconify/react";
import { user } from "@/services/user/user-service";
import { ReviewFeedback } from "@interfaces/document";
import { usePermanentStore } from "@/contexts/permanentStore";
import { useTemporaryStore } from "@/contexts/temporaryStore";

const ReviewFeedbackModal = () => {
  const { id: documentId } = useParams();

  const {
    user: currentUser,
  } = useAppState((s) => ({
    user: (s as AuthState).user,
  }));

  const { showReviewFeedbackDialog, setShowReviewFeedbackDialog, reviewFeedback, setReviewFeedback, resetReviewFeedback } = useTemporaryStore((s) => ({
    showReviewFeedbackDialog: s.showReviewFeedbackDialog,
    setShowReviewFeedbackDialog: s.setShowReviewFeedbackDialog,
    reviewFeedback: s.reviewFeedback,
    setReviewFeedback: s.setReviewFeedback,
    resetReviewFeedback: s.resetReviewFeedback,
  }));

  const { shouldShowAutoFeedbackModal, autoFeedbackModalTimerStart, setAutoFeedbackModalTimerStart, setShouldShowAutoFeedbackModal } = usePermanentStore((s) => ({
    shouldShowAutoFeedbackModal: s.shouldShowAutoFeedbackModal,
    autoFeedbackModalTimerStart: s.autoFeedbackModalTimerStart,
    setAutoFeedbackModalTimerStart: s.setAutoFeedbackModalTimerStart,
    setShouldShowAutoFeedbackModal: s.setShouldShowAutoFeedbackModal,
  }));

  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');

  const handleCloseDialog = () => {
    setShowReviewFeedbackDialog(false);
    resetReviewFeedback();
    setMessage('');
  };

  const onSubmit = async () => {
    setLoading(true);

    const feedbackData: ReviewFeedback = {
      ...reviewFeedback,

      user_id: currentUser.id,
      email: currentUser.email,
      message: message,
    };

    await user.sendReviewFeedbackToServer(feedbackData).then(() => {
      toast.success('Thank you for your feedback!');
      handleCloseDialog();
    }).catch((err) => {
      console.error('Error submitting feedback', err);
      toast.error('Failed to submit feedback. Please try again.');
    });

    setLoading(false);
  }

  // After 2 minutes of active editing or AI use (once per session) this modal will auto show.
  //  Session = a single login instance. The feedback modal is triggered once per session (after 2 minutes of active editing or AI use) and does not reappear unless the user logs out and logs back in.
  useEffect(() => {
    if (shouldShowAutoFeedbackModal === false || autoFeedbackModalTimerStart === null) return;


    const checkTimeAndShowDialog = () => {
      const currentTime = new Date();
      const timeDifference = currentTime.getTime() - new Date(autoFeedbackModalTimerStart).getTime();
      const seconds = Math.floor(timeDifference / 1000);

      if (seconds >= 120) {
        setReviewFeedback({
          feedback_source: 'auto_modal_trigger',
        })
        setShowReviewFeedbackDialog(true);
        setAutoFeedbackModalTimerStart(null);
        setShouldShowAutoFeedbackModal(false);
      }
    };

    checkTimeAndShowDialog();

    const interval = setInterval(checkTimeAndShowDialog, 5000);

    return () => {
      clearInterval(interval);
    };
  }, [autoFeedbackModalTimerStart]);

  return (
    <>
      <Modal
        isOpen={showReviewFeedbackDialog}
        onClose={handleCloseDialog}
        size="2xl"
        placement="top-center"
        className="max-w-[35rem] rounded-[0.25rem]"
        hideCloseButton
      >
        <ModalContent>
          <ModalHeader className="flex items-center justify-between gap-1 border-b border-gray-200 px-8 font-normal text-base">
            How can we improve Cobrief?

            <Icon icon="material-symbols:close-rounded" className="w-5 h-5 cursor-pointer text-gray-500" onClick={handleCloseDialog} />
          </ModalHeader>

          <ModalBody className="px-8 pt-6 gap-4">
            Your feedback helps Cobrief improve. Share what felt confusing, broken, or unhelpful — big or small.

            <Textarea
              label="What do you want to see? How can we make it better?"
              fullWidth
              value={message}
              classNames={{
                inputWrapper: "border border-gray-200 rounded-[0.25rem]",
              }}
              onChange={(e) => setMessage(e.target.value)}
            />
          </ModalBody>

          <ModalFooter className="px-8 pb-8">
            <Button
              isLoading={loading}
              isDisabled={!message}
              color="primary"
              variant="flat"
              className="border border-[#99cc15] bg-[#c6f648] text-black rounded-[0.25rem]"
              onPress={onSubmit}>
              Send
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <ShadCnButton variant="light" onClick={() => {
        setReviewFeedback({
          feedback_source: 'manual_feedback_button',
        })
        setShowReviewFeedbackDialog(true)
      }} className="fixed bottom-4 right-4 w-9 h-9 p-0 grid place-items-center">
        <Icon icon="uiw:message" className="w-5 h-5" />
      </ShadCnButton>
    </>

  )
}

export default ReviewFeedbackModal;